export type CoachMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: number;
};

export type CoachThread = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: CoachMessage[];
};

const KEY = "lendytrack.coach.threads.v1";

function uid() {
  return (
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
}

function read(): CoachThread[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as CoachThread[]) : [];
  } catch {
    return [];
  }
}

function write(threads: CoachThread[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(threads));
}

export function listThreads(): CoachThread[] {
  return read().sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getThread(id: string): CoachThread | undefined {
  return read().find((t) => t.id === id);
}

export function createThread(): CoachThread {
  const now = Date.now();
  const t: CoachThread = {
    id: uid(),
    title: "New conversation",
    createdAt: now,
    updatedAt: now,
    messages: [
      {
        id: uid(),
        role: "assistant",
        content:
          "Hi, I'm your LendyTrack Coach. Ask me anything about loans — APR, fees, lender risks, debt strategies. I'll explain things in plain English. (Educational only — not financial or legal advice.)",
        createdAt: now,
      },
    ],
  };
  write([t, ...read()]);
  return t;
}

export function deleteThread(id: string) {
  write(read().filter((t) => t.id !== id));
}

export function appendMessage(threadId: string, msg: Omit<CoachMessage, "id" | "createdAt">): CoachThread | undefined {
  const all = read();
  const t = all.find((x) => x.id === threadId);
  if (!t) return;
  const full: CoachMessage = { ...msg, id: uid(), createdAt: Date.now() };
  t.messages.push(full);
  t.updatedAt = Date.now();
  if (t.title === "New conversation" && msg.role === "user") {
    t.title = msg.content.slice(0, 60);
  }
  write(all);
  return t;
}

// Mock assistant reply — keyword-based, educational
export function mockAssistantReply(prompt: string): string {
  const p = prompt.toLowerCase();
  if (/apr/.test(p)) {
    return "APR (Annual Percentage Rate) is the true yearly cost of a loan, including interest plus most fees. A simple test: if APR is above ~36%, you're in subprime/predatory territory. Mainstream personal loans run 7–25% APR for fair-to-excellent credit.";
  }
  if (/payday|short.?term/.test(p)) {
    return "Payday loans average 391% APR. They're designed to be rolled over — most borrowers pay more in fees than they originally borrowed. Safer alternatives: a 0% APR credit card cash advance, a credit union PAL (Payday Alternative Loan, capped at 28% APR), or asking your employer about earned-wage access.";
  }
  if (/utilization|credit card|credit score/.test(p)) {
    return "Credit utilization is your balance ÷ available credit. Keeping it under 30% (ideally under 10%) is one of the fastest ways to raise your FICO. Paying down a card from 78% to 25% can add 30–50 points within one billing cycle.";
  }
  if (/snowball|avalanche|debt/.test(p)) {
    return "Avalanche pays the highest-APR debt first — saves the most money. Snowball pays the smallest balance first — builds momentum. For most people: avalanche if you're disciplined, snowball if you need motivation.";
  }
  if (/opploans|payday|rise|ace/.test(p)) {
    return "That lender carries elevated risk in our analysis — typically triple-digit APRs and frequent complaints about hidden fees or aggressive collections. Before signing, check our lender profile and run the offer through the Loan Analyzer.";
  }
  if (/sofi|upstart|marcus|discover|lightstream/.test(p)) {
    return "That's one of the mainstream lenders we rate well. Still: pre-qualify with a soft credit pull, compare at least two offers, and watch the origination fee — it's deducted from the loan proceeds.";
  }
  return "Great question. The short answer: compare at least three lenders, ignore the monthly payment in marketing copy (it hides total cost), and never sign anything with APR above 36%. Want me to walk through a specific lender or offer?";
}
